import { useLayoutEffect, useState } from "react";
import { resizeObserverManager } from "@app/shared/qes-standards/utility/ResizeObserverManager";

export interface MeasuredSize {
  heightPx: number;
  widthPx: number;
}

const UNMEASURED: MeasuredSize = { heightPx: 0, widthPx: 0 };

/**
 * Tracks an element's rendered content-box size in px, so the bar chart's orientation, margins
 * and label font size can be solved from real pixels rather than assumptions. Returns zeroes
 * until the element has been measured. Kept next to its only consumer — promote it to
 * shared/hooks if a second one appears.
 *
 * Pass the element itself, not a ref — a callback ref (`ref={setElement}`) re-renders when the
 * node mounts, which a `useRef` would not, so conditionally rendered elements are still measured.
 *
 * Uses clientWidth/clientHeight rather than getBoundingClientRect so a visible scrollbar's
 * gutter is excluded, and returns the previous object when nothing changed so callers can use
 * the result directly as a useMemo dependency.
 */
export const useMeasuredSize = (element: HTMLElement | null): MeasuredSize => {
  const [size, setSize] = useState<MeasuredSize>(UNMEASURED);

  useLayoutEffect(() => {
    if (!element) {
      setSize(current => (current === UNMEASURED ? current : UNMEASURED));
      return;
    }
    let frame = 0;
    const read = () => {
      const heightPx = element.clientHeight;
      const widthPx = element.clientWidth;
      setSize(current => (current.heightPx === heightPx && current.widthPx === widthPx ? current : { heightPx, widthPx }));
    };
    // A resize can take more than one frame to settle: reacting to the first notification can
    // re-render into a layout that adds or removes a scrollbar, which resizes this element
    // again, and the browser defers that second notification rather than looping. Without the
    // re-read the stale size survives until some unrelated render happens to flush it — a
    // tooltip, say. Re-reading next frame converges on its own, because an unchanged
    // measurement returns the previous object and stops the chain.
    const measure = () => {
      read();
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(read);
    };
    measure();
    resizeObserverManager.observe(element, measure);
    return () => {
      cancelAnimationFrame(frame);
      resizeObserverManager.unobserve(element);
    };
  }, [element]);

  return size;
};
